"use client";

import { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import { SimplifiedUser } from "@/app/types";
import useRegisterModal from "@/app/hooks/useRegisterModal";

const STORAGE_KEY = "beUnderwaterSignupPromoSeen";
/** Cookie read by POST /api/register/diver only (not centers). */
const SIGNUP_PROMO_COOKIE = "beUnderwaterSignupPromo";
const SIGNUP_PROMO_MAX_AGE_SEC = 3600;

interface PromoModalProps {
  currentUser?: SimplifiedUser | null;
}

const PromoModal: React.FC<PromoModalProps> = ({ currentUser }) => {
  const [showModal, setShowModal] = useState(false);
  const registerModal = useRegisterModal();

  useEffect(() => {
    if (currentUser) return;

    if (typeof window === "undefined") return;
    if (localStorage.getItem(STORAGE_KEY) === "1") return;

    const timer = setTimeout(() => {
      setShowModal(true);
      localStorage.setItem(STORAGE_KEY, "1");
    }, 5000);

    return () => clearTimeout(timer);
  }, [currentUser]);

  const handleClose = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleRegister = useCallback(() => {
    if (typeof window !== "undefined") {
      document.cookie = `${SIGNUP_PROMO_COOKIE}=1; path=/; max-age=${SIGNUP_PROMO_MAX_AGE_SEC}; SameSite=Lax`;
    }
    setShowModal(false);
    registerModal.onOpen();
  }, [registerModal]);

  if (!showModal) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-x-hidden overflow-y-auto bg-black/50 p-4">
      <div className="relative w-full max-w-md">
        <div className="relative rounded-lg border-2 border-cyan-700 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-gray-200 p-4">
            <h3 className="text-xl font-bold text-gray-900">
              Register now & save 5%
            </h3>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-full p-1 transition-colors hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          <div className="space-y-4 p-6">
            <p className="text-center text-gray-700">
              Create your free diver account and we&apos;ll email you a{" "}
              <strong>5% discount code</strong> to use on your first booking.
            </p>

            <div className="rounded-lg bg-cyan-700 p-4 text-center text-white">
              <div className="text-3xl font-bold">5% OFF</div>
              <div className="mt-2 text-sm opacity-90">
                Your personal code arrives in your verification email
              </div>
            </div>

            <p className="text-center text-xs text-gray-500">
              Minimum purchase $50 USD. New diver accounts only. Code not shown
              here — check your inbox after signing up.
            </p>
          </div>

          <div className="flex gap-3 p-6 pt-0">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-600 transition-colors hover:bg-gray-50"
            >
              Maybe later
            </button>
            <button
              type="button"
              onClick={handleRegister}
              className="flex-1 rounded-lg bg-cyan-700 px-4 py-2 font-semibold text-white transition-colors hover:bg-cyan-800"
            >
              Register now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoModal;
